import type { AlgoliaAutocompleteOption } from "core/types";
import { Controller, useFormContext } from "react-hook-form";
import {
  AlgoliaAutocomplete,
  type AlgoliaAutocompleteProps,
} from "./autocomplete-algolia";
/**
 * React Hook Form bridge around {@link AlgoliaAutocomplete}: binds `selectedOption` /
 * `onSelectedOptionChange` to a field (`TItem | null`). Use inside `FormProvider`.
 *
 * For controlled search text without RHF, pass `inputValue` and `onInputChange` on
 * `AlgoliaAutocomplete` directly.
 *
 * Interaction coverage lives in `autocomplete-algolia.test.tsx` (same folder).
 */
export type ControlledAlgoliaAutocompleteProps<
  THit,
  TItem extends AlgoliaAutocompleteOption = AlgoliaAutocompleteOption,
> = Omit<
  AlgoliaAutocompleteProps<THit, TItem>,
  "onSelectedOptionChange" | "selectedOption"
> & {
  name: string;
  /** Runs after RHF `field.onChange(option)` (e.g. mirror selection into sibling paths). */
  onAfterSelectedOptionChange?: (option: TItem | null) => void;
  /** Map stored form values to the option shown in the list (legacy / API shapes). */
  selectedOptionFromFieldValue?: (raw: unknown) => TItem | null;
};

export function ControlledAlgoliaAutocomplete<
  THit,
  TItem extends AlgoliaAutocompleteOption = AlgoliaAutocompleteOption,
>({
  name,
  onAfterSelectedOptionChange,
  selectedOptionFromFieldValue,
  ...props
}: ControlledAlgoliaAutocompleteProps<THit, TItem>) {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onBlur, onChange, ref: _ref, value, ...restField },
        fieldState,
      }) => {
        const errorMessage = fieldState?.error?.message;
        const isInvalid = Boolean(errorMessage);

        const selectedOption =
          selectedOptionFromFieldValue != null
            ? selectedOptionFromFieldValue(value)
            : (value as TItem | null | undefined) ?? null;

        return (
          <AlgoliaAutocomplete<THit, TItem>
            {...props}
            {...restField}
            errorMessage={errorMessage}
            isInvalid={isInvalid}
            name={name}
            selectedOption={selectedOption}
            onBlur={onBlur}
            onSelectedOptionChange={(opt) => {
              onChange(opt);
              onAfterSelectedOptionChange?.(opt);
            }}
          />
        );
      }}
    />
  );
}

ControlledAlgoliaAutocomplete.displayName =
  "Recare.ControlledAlgoliaAutocomplete";
